import { useCallback, useEffect, useMemo, useState } from 'react'
import { Appearance } from 'react-native'
import { useSelector } from 'react-redux'
import { darkModeStoreStateSelector } from '@src/selectors/settings'
import { ThemeName } from '@src/style/types'
import { DarkMode } from '@src/screens/Settings/models/settings.types'
import { getTheme } from '@src/style'

const useDefaultThemeName = () => {
  const [mode, setMode] = useState(
    () => (Appearance.getColorScheme() || ThemeName.Light) as ThemeName,
  )

  const onAppearancePreferencesChange = useCallback(() => {
    // using value from params is not reliable
    // see: https://github.com/facebook/react-native/issues/30484
    const colorScheme = Appearance.getColorScheme()
    if (colorScheme === mode) {
      return
    }
    setMode((colorScheme || ThemeName.Light) as ThemeName)
  }, [mode])

  useEffect(() => {
    const appearanceListener = Appearance.addChangeListener(onAppearancePreferencesChange)

    return () => {
      appearanceListener.remove()
    }
  }, [onAppearancePreferencesChange])

  return mode
}

const useThemeName = () => {
  const darkModeSetting = useSelector(darkModeStoreStateSelector)
  const defaultThemeName = useDefaultThemeName()

  const getThemeName = () => {
    switch (darkModeSetting) {
      case DarkMode.On:
        return ThemeName.Dark
      case DarkMode.Off:
        return ThemeName.Light
      case DarkMode.Auto:
      default:
        return defaultThemeName
    }
  }

  return getThemeName()
}

export const useTheme = () => {
  const themeName = useThemeName()

  const theme = useMemo(() => getTheme(themeName), [themeName])

  return {
    theme,
    themeName,
  }
}

export const useIsDarkMode = () => {
  const themeName = useThemeName()

  return themeName === ThemeName.Dark
}
